import PropTypes from 'prop-types';
import TaskNumber from 'contents/ProtocolEditor/Components/TaskNumber';
import '../../Task.scss';
import {useDispatch, useSelector} from 'react-redux';
import {getLabwareDataById} from 'stores/selectors/labwareStateSelectors';
import {getRuntimeStatus} from 'stores/selectors/runtimeSelector';
import {getDeepWellThreshold} from 'stores/selectors/configSelectors';
import Spinner from 'components/Spinner/Spinner';
import TaskExecutedTick from 'components/TaskExecutedTick';
import TaskWrapper from 'contents/ProtocolEditor/TaskList/Task/Components/TaskWrapper';
import TaskTitle from 'contents/ProtocolEditor/TaskList/Task/Components/TaskTitle';
import TaskAction from 'contents/ProtocolEditor/TaskList/Task/Components/TaskAction';
import TaskPipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/Task/Components/TaskPipettingProfileWrapper';
import {CardType, ExecutionStatus, ProtocolEditorViewMode, RuntimeMode, TaskType} from 'constants/dataEnum';
import {setIsEditMixing, setSelectedMenu, setSimpleTransferEditedType} from 'stores/actions/protocolEditorAction';
import {saveMixingStateBeforeEdit} from 'stores/business/taskListMixingBusiness';
import {getAspirateDispenseWellGroupName} from '../../SimpleTransfer/Tasks/simpleTransferTaskHelper';
import {onEditSimpleTransferHandler} from '../../SimpleTransfer/Tasks/simpleTransferTaskBusiness';
import TaskMixingDescription from '../../Components/TaskMixingDescription';
import MixingPipettingProfile from './MixingPipettingProfile';
import {onEditMixingHandler} from './mixingTaskBusiness';

function MixingTaskDetails({
  task,
  runtimeMode,
  readOnly,
  executionStatus,
  onShowMenuContext,
  onDelete,
  onCheckedChange,
  onSelectTask,
  card,
  // #region dragging business
  onDrag,
  isDragging,
  disableDrag,
}) {
  const dispatch = useDispatch();

  const labware = useSelector(getLabwareDataById(task.labwareID));
  const runtimeStatus = useSelector(getRuntimeStatus);
  const deepWellThreshold = useSelector(getDeepWellThreshold);

  const {
    ID,
    mixingVolume,
    number,
    type,
    row,
    column,
    rowTipCount,
    columnTipCount,
    labwareIndex,
    isSelected,
    isIgnored,
  } = task || {};

  const {name: labwareName, rowCount: labwareRowCount, colCount: labwareColumnCount} = labware || {};

  const wellGroupName = getAspirateDispenseWellGroupName(
    row,
    column,
    rowTipCount,
    columnTipCount,
    labwareRowCount,
    labwareColumnCount,
    labwareIndex,
  );

  const isDeepWell = labware.wellDepth > deepWellThreshold;

  const isShowPipettingProfile =
    !readOnly &&
    ((runtimeMode && runtimeStatus === RuntimeMode.PAUSED && executionStatus === ExecutionStatus.NONE) || !runtimeMode);

  const onEdit = async () => {
    if (card.type === CardType.MIXING) {
      await dispatch(saveMixingStateBeforeEdit());
      await dispatch(setSelectedMenu(ProtocolEditorViewMode.MIXING));
      await dispatch(setIsEditMixing(true));
      onSelectTask();
      const callback = () => {
        onSelectTask(null, {bypassValidityChecking: true});
        dispatch(setSelectedMenu(ProtocolEditorViewMode.SIMPLE_TRANSFER));
        dispatch(setIsEditMixing(false));
      };
      dispatch(onEditMixingHandler(task, card.ID, callback));
    } else {
      dispatch(setSimpleTransferEditedType(type));
      const callback = () => {
        onSelectTask(null, {bypassValidityChecking: true});
        dispatch(setSimpleTransferEditedType(null));
      };
      dispatch(onEditSimpleTransferHandler(task, card.ID, callback));
    }
  };

  return (
    <TaskWrapper
      disableDrag={disableDrag}
      onDrag={onDrag}
      isDragging={isDragging}
      isSelected={isSelected}
      onContextMenu={(e) =>
        onShowMenuContext && onShowMenuContext(e, task.ID, CardType.SIMPLE_TRANSFER, [task], card.ID)
      }
      task={task}
      cardId={card.ID}
    >
      <TaskNumber number={number} isHighlighted={isSelected} />
      <TaskTitle
        isDeepWell={isDeepWell}
        title={`${type} ${mixingVolume} μL`}
        description={<TaskMixingDescription labwareName={labwareName} wellGroup={wellGroupName} />}
      />
      {!runtimeMode && !readOnly && (
        <TaskAction
          onEdit={onEdit}
          onDelete={() => onDelete(ID)}
          isIgnored={isIgnored}
          onIgnoreCheckboxChanged={(val) => onCheckedChange(ID, val)}
        />
      )}
      {isShowPipettingProfile && (
        <TaskPipettingProfileWrapper>
          <MixingPipettingProfile
            task={task}
            runtimeMode={runtimeMode}
            showRetractSpeed={card.type === CardType.MIXING || task.type === TaskType.MIXING}
          />
        </TaskPipettingProfileWrapper>
      )}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTING && <Spinner />}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && <TaskExecutedTick />}
    </TaskWrapper>
  );
}

MixingTaskDetails.propTypes = {
  runtimeMode: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  executionStatus: PropTypes.string.isRequired,
  onDrag: PropTypes.func.isRequired,
  disableDrag: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  onShowMenuContext: PropTypes.func.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  card: PropTypes.object,
  task: PropTypes.object.isRequired,
};

MixingTaskDetails.defaultProps = {
  card: {},
};

export default MixingTaskDetails;
